/**
 * useCardDrawerData — read-side state for the kanban CardDetailDrawer:
 * the full-card refetch, similar-cards + schedule-context queries, the
 * snap-to-job mutation, the personal-mirror parent resolution, the
 * focused-card cache writers, and the lane/actor name lookups.
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling).
 * Behaviour is unchanged — every query key, `enabled` gate, staleTime,
 * callback and dependency array is a 1:1 move.
 */
import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { KanbanApi, KanbanMember } from '../../api'
import type { Card, Lane } from '../../types'

export interface UseCardDrawerDataArgs {
  api: KanbanApi
  cardProp: Card | null
  open: boolean
  lanes: Lane[]
  members?: KanbanMember[]
  onChanged?: (card: Card) => void
}

export function useCardDrawerData({
  api,
  cardProp,
  open,
  lanes,
  members,
  onChanged,
}: UseCardDrawerDataArgs) {
  // PERF-2026-06-04-KANBAN-LEAN-FIRST-LOAD — the board loads its cards with
  // the lean projection; the drawer needs the FULL card, so we refetch it by
  // id whenever the drawer is open. react-query dedupes + caches.
  const fullCardQ = useQuery({
    queryKey: ['kanban', 'card', cardProp?.id ?? null],
    queryFn: () => api.getCard(cardProp!.id),
    enabled: open && !!cardProp?.id,
    staleTime: 30_000,
  })
  const card: Card | null = fullCardQ.data ?? cardProp

  const [mergeDialogOpen, setMergeDialogOpen] = useState(false)
  // When the soft-match chip suggests a card to merge with, we open the
  // dialog pre-selecting that loser via this state.
  const [mergePreselectId, setMergePreselectId] = useState<string | null>(null)

  // Soft-match suggestions (item 15 deep). Fires only when the host wired
  // the API and the focused card has been embedded.
  const similarQ = useQuery({
    queryKey: ['kanban', 'card-similar', card?.id],
    queryFn: () => api.getSimilarCards!(card!.id, { k: 3, max_dist: 0.3 }),
    enabled: open && !!card?.id && !!api.getSimilarCards && !!(card as Card | null)?.embedding_model,
    staleTime: 60_000,
  })

  const qc = useQueryClient()

  // PERF-2026-06-04-KANBAN-LEAN-FIRST-LOAD — focused-card mutations return the
  // FULL CardSerializer payload; write it into the focused card's own cache so
  // `fullCardQ` stays both FRESH and FULL. Still bubbles to the parent board.
  const pushFocusedCardUpdate = useCallback(
    (updated: Card) => {
      if (updated?.id) qc.setQueryData(['kanban', 'card', updated.id], updated)
      onChanged?.(updated)
    },
    [qc, onChanged],
  )

  // Pyramid scheduling (items 4/5). Drift chip queries the server for
  // {card window, job window, drift, siblings, rollup}.
  const scheduleCtxQ = useQuery({
    queryKey: ['kanban', 'card-schedule-ctx', card?.id],
    queryFn: () => api.getCardScheduleContext!(card!.id),
    enabled: open && !!card?.id && !!card?.job_id && !!api.getCardScheduleContext,
    staleTime: 30_000,
  })

  const snapMutation = useMutation({
    mutationFn: (cardId: string) => api.snapCardToJob!(cardId),
    onSuccess: (updated) => {
      pushFocusedCardUpdate(updated)
      void scheduleCtxQ.refetch()
    },
  })

  // ── Phase 4 card-view parity ───────────────────────────────────────
  // A PERSONAL MIRROR shares the parent's collaboration surface: its labels,
  // members and chat ARE the team's, only its schedule/status are private.
  const mirrorParentId =
    (card as Card | null)?.personal_mirror_parent_id || null
  const parentQ = useQuery({
    queryKey: ['kanban', 'card', mirrorParentId],
    queryFn: () => api.getCard(mirrorParentId!),
    enabled: open && !!mirrorParentId,
    staleTime: 30_000,
  })
  // Collab edits must NOT swap the drawer away from the mirror — they refresh
  // the parent cache instead of bubbling to the drawer's card.
  const onCollabChanged = useCallback(
    (updated: Card) => {
      if (mirrorParentId) {
        qc.setQueryData(['kanban', 'card', mirrorParentId], updated)
        void parentQ.refetch()
      } else {
        // BUG-2026-06-29-KANBAN-DRAWER-COLLAB-STALE — also write the focused-
        // card cache so the avatars / tag chips don't stay stale.
        if (updated?.id) qc.setQueryData(['kanban', 'card', updated.id], updated)
        onChanged?.(updated)
      }
    },
    [mirrorParentId, qc, parentQ, onChanged],
  )

  // Pre-resolve a lane-id → lane-name lookup for the audit log.
  const laneNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const l of lanes) m.set(l.id, l.name)
    return m
  }, [lanes])

  // Resolve an audit row's `by` to a human name for the activity trail.
  const actorNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const mem of members ?? []) {
      m.set(String(mem.id), (mem.name || '').trim() || mem.email || `User #${mem.id}`)
    }
    return m
  }, [members])
  const resolveActorName = useCallback(
    (by: unknown): string | null => {
      if (by === null || by === undefined || by === '') return null
      const key = String(by)
      return actorNameById.get(key) ?? `User #${key}`
    },
    [actorNameById],
  )

  return {
    card,
    mergeDialogOpen, setMergeDialogOpen,
    mergePreselectId, setMergePreselectId,
    similarQ,
    qc,
    pushFocusedCardUpdate,
    scheduleCtxQ,
    snapMutation,
    mirrorParentId,
    parentQ,
    onCollabChanged,
    laneNameById,
    resolveActorName,
  }
}
